import { useState, useEffect } from 'react';

import { Icon } from '@iconify/react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { formatDate } from '@core/i18n/format';

import {
  useGetAgentSoulQuery,
  useUpdateAgentSoulMutation,
  useGetAgentSkillsQuery,
  useGetAgentLearningsQuery,
  useGetAgentMemoryQuery,
  useGetAgentMemoryPageQuery,
  useUpdateAgentMemoryPageMutation,
  useForgetAgentMemoryPageMutation,
  useRestoreAgentMemoryPageMutation,
} from '../api/aiApi';

const SCOPE_LABELS = {
  user: 'ai.agentSettings.memory.scopeLabel.user',
  project: 'ai.agentSettings.memory.scopeLabel.project',
  organization: 'ai.agentSettings.memory.scopeLabel.organization',
};
const SCOPE_IMPACTS = {
  user: 'ai.agentSettings.memory.scopeImpact.user',
  project: 'ai.agentSettings.memory.scopeImpact.project',
  organization: 'ai.agentSettings.memory.scopeImpact.organization',
};
const SCOPE_BADGES = {
  user: 'ai.agentSettings.memory.scopeBadge.user',
  project: 'ai.agentSettings.memory.scopeBadge.project',
  organization: 'ai.agentSettings.memory.scopeBadge.organization',
};
// Any scope other than user or project is organization-wide.
const scopeKey = (scope) => (scope === 'user' || scope === 'project' ? scope : 'organization');

export function AgentSettingsPage() {
  const { t } = useTranslation();
  const { data: soulData, isLoading: soulLoading } = useGetAgentSoulQuery();
  const { data: skills = [], isLoading: skillsLoading } = useGetAgentSkillsQuery();
  const { data: learnings, isLoading: learningsLoading } = useGetAgentLearningsQuery();
  const [updateSoul, { isLoading: isSaving }] = useUpdateAgentSoulMutation();

  const [soulContent, setSoulContent] = useState('');
  const [activeTab, setActiveTab] = useState('memory');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (soulData?.content) {
      setSoulContent(soulData.content);
    }
  }, [soulData]);

  const handleSaveSoul = async () => {
    await updateSoul({ content: soulContent }).unwrap();
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const tabs = [
    { id: 'memory', label: t('ai.agentSettings.tabs.memory') },
    { id: 'soul', label: 'SOUL' },
    { id: 'skills', label: t('ai.agentSettings.tabs.skills') },
    { id: 'learnings', label: t('ai.agentSettings.tabs.learnings') },
  ];

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-xl font-bold text-[var(--color-text)] mb-1">{t('ai.agentSettings.title')}</h1>
      <p className="text-sm text-[var(--color-text-muted)] mb-6">
        {t('ai.agentSettings.subtitle')}
      </p>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-[var(--color-border)]">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-[var(--color-accent)] text-[var(--color-accent)]'
                : 'border-transparent text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
            }`}
          >
            {tab.label}
            {tab.id === 'skills' && skills.length > 0 && (
              <span className="ml-1.5 text-xs bg-[var(--color-bg-muted)] px-1.5 py-0.5 rounded-full">
                {skills.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* SOUL Tab */}
      {activeTab === 'memory' && <CorporateMemoryPanel />}

      {/* SOUL Tab */}
      {activeTab === 'soul' && (
        <div>
          {soulLoading ? (
            <p className="text-sm text-[var(--color-text-muted)]">{t('ai.agentSettings.loading')}</p>
          ) : (
            <>
              <textarea
                value={soulContent}
                onChange={(e) => setSoulContent(e.target.value)}
                rows={20}
                className="w-full font-mono text-sm p-4 border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] resize-y"
                placeholder={t('ai.agentSettings.soulPlaceholder')}
              />
              <div className="mt-3 flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleSaveSoul}
                  disabled={isSaving}
                  className="py-2 px-6 text-sm font-medium text-white bg-[var(--color-accent)] rounded-lg hover:opacity-90 disabled:opacity-50 transition-opacity"
                >
                  {isSaving ? t('ai.agentSettings.saving') : t('ai.agentSettings.saveSoul')}
                </button>
                {saved && (
                  <span className="text-sm text-green-600">{t('ai.agentSettings.saved')}</span>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Skills Tab */}
      {activeTab === 'skills' && (
        <div>
          {skillsLoading ? (
            <p className="text-sm text-[var(--color-text-muted)]">{t('ai.agentSettings.loading')}</p>
          ) : skills.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-sm text-[var(--color-text-muted)]">
                {t('ai.agentSettings.noSkills')}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {skills.map((skill) => (
                <div
                  key={skill.name}
                  className="border border-[var(--color-border)] rounded-lg p-4"
                >
                  <h3 className="text-sm font-semibold text-[var(--color-text)] mb-2">
                    {skill.name}
                  </h3>
                  <pre className="text-xs font-mono text-[var(--color-text-secondary)] whitespace-pre-wrap bg-[var(--color-bg-muted)] p-3 rounded">
                    {skill.content}
                  </pre>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Learnings Tab */}
      {activeTab === 'learnings' && (
        <div>
          {learningsLoading ? (
            <p className="text-sm text-[var(--color-text-muted)]">{t('ai.agentSettings.loading')}</p>
          ) : (
            <div className="space-y-6">
              <LearningSection heading="Learnings" title={t('ai.agentSettings.learnings.learnings')} content={learnings?.learnings} />
              <LearningSection heading="Errors" title={t('ai.agentSettings.learnings.errors')} content={learnings?.errors} />
              <LearningSection heading="Feature Requests" title={t('ai.agentSettings.learnings.featureRequests')} content={learnings?.feature_requests} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function getMemoryPages(data) {
  if (Array.isArray(data)) return data;
  const activePages = data?.results || data?.pages || [];
  const activeNames = new Set(activePages.map((page) => page.name));
  const deletedPages = (data?.deleted_pages || [])
    .map((page) => typeof page === 'string' ? { name: page, is_deleted: true } : { ...page, is_deleted: true })
    .filter((page) => !activeNames.has(page.name));
  return [...activePages, ...deletedPages];
}

function CorporateMemoryPanel() {
  const { t, i18n } = useTranslation();
  const [search, setSearch] = useState('');
  // A "[[page-name]]" in chat links here with ?page=. Read it once, on mount:
  // the deep link seeds the selection and the user takes it from there.
  const [selectedName, setSelectedName] = useState(
    () => new URLSearchParams(window.location.search).get('page') || null,
  );
  const [draft, setDraft] = useState('');
  const [notice, setNotice] = useState('');
  const [actionError, setActionError] = useState('');
  const { data, isLoading, isError } = useGetAgentMemoryQuery(search);
  const pages = getMemoryPages(data);
  const selectedSummary = pages.find((page) => page.name === selectedName);
  const isSelectedDeleted = Boolean(selectedSummary?.deleted_at || selectedSummary?.is_deleted);
  const { data: selectedPage, isFetching: isLoadingPage } = useGetAgentMemoryPageQuery(
    selectedName,
    { skip: !selectedName || isSelectedDeleted },
  );
  const [updatePage, { isLoading: isSavingMemory }] = useUpdateAgentMemoryPageMutation();
  const [forgetPage, { isLoading: isForgetting }] = useForgetAgentMemoryPageMutation();
  const [restorePage, { isLoading: isRestoring }] = useRestoreAgentMemoryPageMutation();
  const selectedMemory = selectedPage || selectedSummary;
  const canManageSelected = selectedMemory?.can_manage !== false;
  const selectedScopeLabel = t(SCOPE_LABELS[scopeKey(selectedMemory?.scope)]);
  const selectedScopeImpact = t(SCOPE_IMPACTS[scopeKey(selectedMemory?.scope)]);

  useEffect(() => {
    setDraft(selectedPage?.content || selectedSummary?.content || '');
  }, [selectedPage, selectedSummary?.content]);

  const showNotice = (message) => {
    setNotice(message);
    window.setTimeout(() => setNotice(''), 3000);
  };

  const handleSave = async () => {
    setActionError('');
    try {
      await updatePage({ name: selectedName, content: draft }).unwrap();
      showNotice(t('ai.agentSettings.memory.updated'));
    } catch {
      setActionError(t('ai.agentSettings.memory.saveFailed'));
    }
  };

  const handleForget = async () => {
    if (!window.confirm(t('ai.agentSettings.memory.forgetConfirm', { name: selectedName, impact: selectedScopeImpact }))) return;
    setActionError('');
    try {
      await forgetPage(selectedName).unwrap();
      showNotice(t('ai.agentSettings.memory.forgotten'));
    } catch {
      setActionError(t('ai.agentSettings.memory.forgetFailed'));
    }
  };

  const handleRestore = async () => {
    setActionError('');
    try {
      await restorePage(selectedName).unwrap();
      showNotice(t('ai.agentSettings.memory.restored'));
    } catch {
      setActionError(t('ai.agentSettings.memory.restoreFailed'));
    }
  };

  return (
    <div data-tour="knowledge-base" className="grid min-h-[520px] grid-cols-1 overflow-hidden rounded-xl border border-[var(--color-border)] bg-white md:grid-cols-[minmax(220px,0.85fr)_minmax(0,1.6fr)]">
      <section className="border-b border-[var(--color-border)] md:border-b-0 md:border-r">
        <div className="border-b border-[var(--color-border)] p-4">
          <h2 className="font-semibold text-[var(--color-text)]">{t('ai.agentSettings.memory.heading')}</h2>
          <p className="mt-1 text-xs leading-5 text-[var(--color-text-muted)]">
            {t('ai.agentSettings.memory.intro')}
          </p>
          <label className="relative mt-3 block">
            <Icon icon="mdi:magnify" className="absolute left-3 top-2.5 size-4 text-[var(--color-text-muted)]" />
            <span className="sr-only">{t('ai.agentSettings.memory.searchLabel')}</span>
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t('ai.agentSettings.memory.searchPlaceholder')}
              className="w-full rounded-lg border border-[var(--color-border)] py-2 pl-9 pr-3 text-sm focus:border-[var(--color-accent)] focus:outline-none focus:ring-1 focus:ring-[var(--color-accent)]"
            />
          </label>
        </div>

        <div className="max-h-[440px] overflow-y-auto p-2">
          {isLoading ? (
            <p className="p-3 text-sm text-[var(--color-text-muted)]">{t('ai.agentSettings.memory.loading')}</p>
          ) : isError ? (
            <p className="p-3 text-sm text-red-600">{t('ai.agentSettings.memory.loadFailed')}</p>
          ) : pages.length === 0 ? (
            <div className="px-3 py-10 text-center">
              <Icon icon="mdi:brain" className="mx-auto size-7 text-[var(--color-text-muted)]" />
              <p className="mt-2 text-sm font-medium text-[var(--color-text)]">
                {search ? t('ai.agentSettings.memory.noMatches') : t('ai.agentSettings.memory.empty')}
              </p>
              {!search && <p className="mt-1 text-xs leading-5 text-[var(--color-text-muted)]">{t('ai.agentSettings.memory.emptyHint')}</p>}
            </div>
          ) : pages.map((page) => {
            const isDeleted = Boolean(page.deleted_at || page.is_deleted);
            return (
              <button
                key={page.name}
                type="button"
                onClick={() => setSelectedName(page.name)}
                className={`mb-1 w-full rounded-lg px-3 py-2.5 text-left transition-colors ${selectedName === page.name ? 'bg-[var(--color-accent-subtle)]' : 'hover:bg-[var(--color-bg-muted)]'}`}
              >
                <span className="flex items-center gap-2">
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-[var(--color-text)]">{page.title || page.name}</span>
                  {!isDeleted && page.scope && (
                    <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700">
                      {t(SCOPE_BADGES[scopeKey(page.scope)])}
                    </span>
                  )}
                  {isDeleted && <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500">{t('ai.agentSettings.memory.forgottenBadge')}</span>}
                </span>
                {page.description && <span className="mt-1 block line-clamp-2 text-xs leading-4 text-[var(--color-text-muted)]">{page.description}</span>}
              </button>
            );
          })}
        </div>
      </section>

      <section className="min-w-0 p-5">
        {!selectedName ? (
          <div className="flex h-full flex-col items-center justify-center text-center">
            <Icon icon="mdi:book-open-page-variant-outline" className="size-9 text-[var(--color-text-muted)]" />
            <h3 className="mt-3 text-sm font-semibold text-[var(--color-text)]">{t('ai.agentSettings.memory.selectTitle')}</h3>
            <p className="mt-1 max-w-sm text-xs leading-5 text-[var(--color-text-muted)]">{t('ai.agentSettings.memory.selectHint')}</p>
          </div>
        ) : isLoadingPage && !isSelectedDeleted ? (
          <p className="text-sm text-[var(--color-text-muted)]">{t('ai.agentSettings.memory.loading')}</p>
        ) : (
          <>
            <div className="mb-4 flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="truncate text-base font-semibold text-[var(--color-text)]">{selectedPage?.title || selectedSummary?.title || selectedName}</h3>
                  {isSelectedDeleted && <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">{t('ai.agentSettings.memory.forgottenBadge')}</span>}
                </div>
                <p className="mt-1 text-xs text-[var(--color-text-muted)]">{selectedScopeLabel} · {selectedPage?.updated_at
                    ? t('ai.agentSettings.memory.updatedOn', { date: formatDate(selectedPage.updated_at, i18n.resolvedLanguage) })
                    : t('ai.agentSettings.memory.availableInFuture')}</p>
                {selectedMemory?.evidence && (
                  <div className="mt-3 rounded-lg bg-[var(--color-bg-muted)] p-3 text-xs leading-5 text-[var(--color-text-secondary)]">
                    <p className="font-medium text-[var(--color-text)]">{t('ai.agentSettings.memory.whyRemembered')}</p>
                    <p>{selectedMemory.evidence}</p>
                    <p className="mt-1 text-[var(--color-text-muted)]">
                      {selectedMemory.reviewer_model
                        ? t('ai.agentSettings.memory.reviewedBy', { model: selectedMemory.reviewer_model })
                        : t('ai.agentSettings.memory.reviewed')}
                      {typeof selectedMemory.confidence === 'number'
                        ? ` · ${t('ai.agentSettings.memory.confidence', { percent: Math.round(selectedMemory.confidence * 100) })}`
                        : ''}
                      {selectedMemory.curation_action ? ` · ${selectedMemory.curation_action}` : ''}
                      {selectedMemory.source_conversation
                        ? ` · ${t('ai.agentSettings.memory.conversation', { id: selectedMemory.source_conversation })}`
                        : ''}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <label className="block text-xs font-medium text-[var(--color-text-secondary)]" htmlFor="memory-content">{t('ai.agentSettings.memory.contentLabel')}</label>
            <textarea
              id="memory-content"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              disabled={isSelectedDeleted || !canManageSelected}
              rows={15}
              className="mt-2 w-full resize-y rounded-lg border border-[var(--color-border)] p-4 font-mono text-sm leading-6 text-[var(--color-text)] focus:border-[var(--color-accent)] focus:outline-none focus:ring-1 focus:ring-[var(--color-accent)] disabled:bg-[var(--color-bg-muted)] disabled:text-[var(--color-text-muted)]"
            />

            <div className="mt-4 flex items-center gap-2">
              {!canManageSelected ? (
                <p className="text-xs text-[var(--color-text-muted)]">{t('ai.agentSettings.memory.readOnly')}</p>
              ) : isSelectedDeleted ? (
                <button type="button" onClick={handleRestore} disabled={isRestoring} className="rounded-lg bg-[var(--color-accent)] px-4 py-2 text-sm font-medium text-white disabled:opacity-50">{isRestoring ? t('ai.agentSettings.memory.restoring') : t('ai.agentSettings.memory.restore')}</button>
              ) : (
                <>
                  <button type="button" onClick={handleSave} disabled={isSavingMemory || draft === (selectedPage?.content || '')} className="rounded-lg bg-[var(--color-accent)] px-4 py-2 text-sm font-medium text-white disabled:opacity-50">{isSavingMemory ? t('ai.agentSettings.saving') : t('ai.agentSettings.memory.saveCorrection')}</button>
                  <button type="button" onClick={handleForget} disabled={isForgetting} className="rounded-lg px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50">{isForgetting ? t('ai.agentSettings.memory.forgetting') : t('ai.agentSettings.memory.forget')}</button>
                </>
              )}
              {notice && <span role="status" className="ml-2 text-xs text-green-700">{notice}</span>}
            </div>
            {actionError && <p role="alert" className="mt-3 text-xs text-red-600">{actionError}</p>}
          </>
        )}
      </section>
    </div>
  );
}

/**
 * `heading` is the English markdown heading the backend writes at the top of
 * an empty learnings file (not displayed); `title` is the translated label.
 */
function LearningSection({ heading, title, content }) {
  const { t } = useTranslation();
  if (!content || content.trim() === `# ${heading}`) {
    return (
      <div>
        <h3 className="text-sm font-semibold text-[var(--color-text)] mb-2">{title}</h3>
        <p className="text-sm text-[var(--color-text-muted)]">{t('ai.agentSettings.learnings.empty')}</p>
      </div>
    );
  }

  return (
    <div>
      <h3 className="text-sm font-semibold text-[var(--color-text)] mb-2">{title}</h3>
      <pre className="text-xs font-mono text-[var(--color-text-secondary)] whitespace-pre-wrap bg-[var(--color-bg-muted)] p-4 rounded-lg max-h-64 overflow-y-auto">
        {content}
      </pre>
    </div>
  );
}

LearningSection.propTypes = {
  heading: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  content: PropTypes.string,
};
